import { CheckboxGroup } from '@chakra-ui/checkbox'
import * as React from 'react'

import { Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'

import { InputCheckbox } from './InputCheckbox'

type TCheckboxInputProps = Pick<
  PropsOf<typeof InputCheckbox>,
  | 'bgColor'
  | 'minHeight'
  | 'isDisabled'
  | 'selectedBgColor'
  | 'checkboxPosition'
  | 'checkboxMarginTop'
>

type TProps<TOption> = NoChildren &
  TCheckboxInputProps & {
    /**
     * if true, render bottom border line under each item
     */
    hasDivider?: PropsOf<typeof InputCheckbox>['hasDivider']
    /**
     * indicator whether it is possible to add more addons
     */
    canAdd?: boolean
    /**
     * array of options that the checkbox buttons should be rendered for
     */
    options: RoA<TOption>
    /**
     * prop of the option that will return string/number id, unique amongst the array
     * used as a key AND for comparing with selected option
     */
    idPropKey: keyof TOption
    /**
     * option from the options array, that is currently selected
     */
    selectedOptions: RoA<TOption>
    /**
     * callback on option click
     */
    onSelect: (options: RoA<TOption>) => void
    /**
     * vertical space between individual items
     */
    spacing?: PropsOf<typeof Stack>['spacing']
    /**
     * option label renderer
     */
    renderOptionLabel: (option: TOption) => React.ReactNode
  }

export const InputCheckboxGroup = <TOption,>({
  hasDivider,
  options,
  onSelect,
  idPropKey,
  canAdd = true,
  selectedOptions,
  renderOptionLabel,
  spacing = `medium`,
  ...checkboxInputProps
}: TProps<TOption>) => {
  const onOptionChange = React.useCallback(
    (optionIds: RoA<string | number>) => {
      const selectedOptions = optionIds
        .map((selectedId) => options.find((option) => String(option[idPropKey]) === selectedId))
        .filter((selectedOption) => selectedOption !== undefined)

      onSelect(selectedOptions as RoA<TOption>)
    },
    [idPropKey, onSelect, options],
  )

  const isDisabledCheckbox = (option: TOption) => {
    if (checkboxInputProps.isDisabled) {
      // all checkbox disabled
      return true
    }

    if (canAdd) {
      return false
    }

    const optionSelected = selectedOptions.find((selectedOption) => {
      return selectedOption[idPropKey] === option[idPropKey]
    })

    // max number of options is selected – allow only unselecting of the already selected & disable the not selected ones
    return !optionSelected
  }

  return (
    <CheckboxGroup
      onChange={onOptionChange}
      value={selectedOptions.map((option) => String(option[idPropKey]))}
    >
      <Grid
        gridTemplateColumns={`repeat(auto-fill, minmax(300px, 1fr))`}
        columnGap={`large`}
        rowGap={spacing}
      >
        {options.map((option) => (
          <InputCheckbox
            {...checkboxInputProps}
            key={String(option[idPropKey])}
            value={String(option[idPropKey])}
            isDisabled={isDisabledCheckbox(option)}
            hasDivider={hasDivider}
            dividerSpacing={spacing}
            isChecked={selectedOptions.some((selectedOption) => {
              return selectedOption[idPropKey] === option[idPropKey]
            })}
          >
            {renderOptionLabel(option)}
          </InputCheckbox>
        ))}
      </Grid>
    </CheckboxGroup>
  )
}
